import React, { Component, Fragment } from 'react';
import { MutationResult } from 'react-apollo';
import { DataProxy } from 'apollo-cache';
import { noop } from 'lodash';
import { Formik } from 'formik';
import { compose } from 'recompose';

import { PlusButton } from 'components';
import { IWithNotify, withNotify } from 'providers';
import { IWithGroupSources, withGroupSources } from 'providers/queries';
import {
  ClientGroupSource,
  ContentApproval,
  GroupSourceType,
  MutationClientGroupSourceCreateArgs,
  SourceStatus,
} from 'api';

import Source from './Source';
import Mutations from './Mutations';
import { SourceSchema } from '../../validations';

interface IProps {
  groupId: string;
}

interface IPropsEnhanced extends IProps, IWithNotify, IWithGroupSources {}

interface IState {
  sources: ClientGroupSource[];
}

const enhancers = compose<IPropsEnhanced, IProps>(withNotify, withGroupSources);

class Sources extends Component<IPropsEnhanced> {
  state: IState;

  constructor(props: IPropsEnhanced) {
    super(props);

    this.state = {
      sources: props.groupSources,
    };
  }

  handleInit = () => {
    const { groupId } = this.props;
    const { sources } = this.state;

    const initValues: MutationClientGroupSourceCreateArgs = {
      group_id: groupId,
      source_status: SourceStatus.Disabled,
      source_type: GroupSourceType.Keyword,
      source_value: '',
      approval: ContentApproval.Submitted,
    };

    sources.unshift(initValues as ClientGroupSource);

    this.setState({ sources });
  };

  handleAdd = (index: number) => (cache: DataProxy, result: MutationResult) => {
    const { sources } = this.state;

    const { data } = result;
    const { clientGroupSourceCreate } = data;

    sources[index] = clientGroupSourceCreate;

    this.setState({ sources });
  };

  handleDelete = (index: number) => () => {
    const { sources } = this.state;

    sources.splice(index, 1);

    this.setState({ sources });
  };

  handleSettingsChange = (index: number) => (cache: DataProxy, result: MutationResult) => {
    const { sources } = this.state;

    const { data } = result;
    const { clientGroupSourceUpdate } = data;

    sources[index] = clientGroupSourceUpdate;

    this.setState({ sources });
  };

  render() {
    const { notify } = this.props;
    const { sources } = this.state;

    return (
      <Fragment>
        <PlusButton i18nKey="groups:sources" onClick={this.handleInit} />
        {sources.map((source, index) => (
          <Mutations key={source.id || index} onError={notify.mutationError}>
            {mutations => (
              <Formik
                initialValues={source}
                onSubmit={noop}
                validationSchema={SourceSchema()}
                render={formikProps => (
                  <Source
                    formikProps={formikProps}
                    mutations={mutations}
                    onDelete={this.handleDelete(index)}
                    onAdd={this.handleAdd(index)}
                    onSettingsChange={this.handleSettingsChange(index)}
                  />
                )}
              />
            )}
          </Mutations>
        ))}
      </Fragment>
    );
  }
}

export default enhancers(Sources);
